import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  LinearProgress,
  List,
  ListItem,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Button,
  Alert,
  AlertTitle,
  TextField,
  Modal,
  FormGroup,
  Checkbox,
  Card,
  CardContent,
  Skeleton,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { CheckCircleOutline, SchoolOutlined } from '@mui/icons-material';
import { useParams } from 'react-router';
import { setSnackError } from '../Reducers/snackErrors';
import { setSuccessSnack } from '../Reducers/snackSuccess';
import { DEV_LINK, AWS_LINK } from '../Static/static';
import AssessmentCompletionMessage from './AssessmentCompletionMessage';
import StudentPreAssessmentForm from './StudentPreAssessment';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';


const AssessmentTaker = () => {
  const { sessionId, studentId, assessmentId } = useParams();
  const link = process.env.NODE_ENV === "production" ? AWS_LINK : DEV_LINK;

  const [questions, setQuestions] = useState([]);
  const [preAssessment, setPreAssessment] = useState(false);
  const [preAssessmentCompleted, setPreAssessmentCompleted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [student, setStudent] = useState({ first_name: '', last_name: '', middle_name: '', grade_level: '' });
  const [error, setError] = useState({ title: null, body: null, open: false });
  const [questionnaireId, setQuestionnaireId] = useState(null);

  // NEW: loading states
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isLoading = loadingStudent || loadingAssessment;

  const dispatch = useDispatch();

  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const isValidUUID = (id) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);

  const [answers, setAnswers] = useState({});

  const handleChoiceSelect = (questionId, choiceId) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: choiceId,
    }));
  };

  const handleImageClick = (url) => {
    setSelectedImage(url);
    setOpenImageModal(true);
  };

  const saveAssessment = async () => {
    try {
      setSubmitting(true); // NEW
      const response = await axios.post(link + "/create_student_assessment_response", {
        session_id: sessionId,
        student_id: Number(studentId),
        assessment_id: Number(assessmentId),
        questionnaire_id: Number(questionnaireId),
        answers,
      });
      if (response.data.status === "OK") {
        dispatch(setSuccessSnack({ title: 'Success!', body: 'Thank you. We have logged your assessment.' }));
        setCompleted(true);
        sessionStorage.setItem('session', "completed");
      }
    } catch (error) {
      setError({ title: 'Error', open: true, body: 'Unable to submit assessment. No more than one submission per session' });
    } finally {
      setSubmitting(false); // NEW
    }
  };

  const getStudentDetails = async () => {
    try {
      if (!studentId || !sessionId) { return false; }
      setLoadingStudent(true); // NEW
      const response = await axios.get(link + "/get_student_details", { params: { student_id: Number(studentId), session_id: sessionId } });
      if (response.data.data) {
        setStudent(response.data.data);
        return true;
      }
      return false;
    } catch (error) {
      setError({ title: 'Error', open: true, body: 'Unable to get student details.' });
      return false;
    } finally {
      setLoadingStudent(false); // NEW
    }
  };

  const loadSavedAssessment = () => {
    try {
      if (!studentId) return;
      const existingSessions = sessionStorage.getItem('session');
      if (existingSessions === "completed") {
        setCompleted(true);
        return;
      }
      if (existingSessions) {
        const sessions = JSON.parse(existingSessions);
        if (sessions[`${studentId}:${assessmentId}`]) {
          setAnswers(sessions[`${studentId}:${assessmentId}`]);
        }
      }
    } catch (error) {
      // swallow; not critical
    }
  };

  const groupQuestions = (flatArray) => {
    const grouped = {};
    flatArray.forEach((item) => {
      const qid = item.question_id;
      if (!grouped[qid]) {
        grouped[qid] = {
          question_id: item.question_id,
          assessment_id: item.assessment_id,
          question_text: item.question_text,
          question_type: item.question_type,
          image_url: item.image_url,
          points: item.points,
          order_number: item.order_number,
          choices: [],
        };
      }
      if (item.choice_id) {
        grouped[qid].choices.push({
          choice_id: item.choice_id,
          choice_text: item.choice_text,
          is_correct: item.is_correct,
          order_number: item.choice_order,
        });
      }
    });
    return Object.values(grouped);
  };

  const handleMultiSelect = (questionId, choiceId, checked) => {
    if (checked) {
      if (questionId in answers) {
        const choices = answers[questionId];
        setAnswers((prev) => ({
          ...prev,
          [questionId]: [...choices, choiceId],
        }));
      } else {
        setAnswers((prev) => ({
          ...prev,
          [questionId]: [choiceId],
        }));
      }
    } else {
      const choices = (answers[questionId] || []).filter((item) => item !== choiceId);
      setAnswers((prev) => ({
        ...prev,
        [questionId]: [...choices],
      }));
    }
  };

  const getAssessments = async () => {
    try {
      setLoadingAssessment(true); 
      const response = await axios.get(link + '/get_assessment_questions_external', {
        params: { assessment_id: assessmentId, student_id: studentId, session_token: sessionId },
      });
      if (response.data.data) {
        const gp = groupQuestions(response.data.data);
        setQuestions(gp);
        setPreAssessment(response.data.pre_assessment);
        setPreAssessmentCompleted(response.data.pre_assessment_completed);
      }
    } catch (error) {
      dispatch( setSnackError({ title: 'Error', open: true, body: 'Unable to get assessments. Please reach out test administrator.' }));
    } finally {
      setLoadingAssessment(false); 
    }
  };

  const handlePreAssessmentFormSubmit = async (data) => {
    const payload = {
      student_id: Number(studentId),
      assessment_id: Number(assessmentId),
      session_token: sessionId,
      ...data,
    };
    try {
      setSubmitting(true); // NEW (reuse submitting for this flow)
      const response = await axios.post(link + "/create_pre_assessment", payload);
      if (response.data.status === "OK") {
        setQuestionnaireId(response.data.id);
        dispatch(setSuccessSnack({ title: 'Thanks!', body: 'Assessment will start shortly!' }));
        setPreAssessmentCompleted(true);
      }
    } catch (error) {
      dispatch(setSnackError({ title: 'Error', open: true, body: 'Unable to record pre-assessment' }));
    } finally {
      setSubmitting(false); // NEW
    }
  };

  useEffect(() => {
    const init = async () => {
      if (assessmentId) {
        try {
          const validSessionStudent = await getStudentDetails();
          if (validSessionStudent) {
            await getAssessments();
            loadSavedAssessment();
          }
        } catch (error) {
          console.error("Failed to load assessment data", error);
        }
      }
    };
    init();
    return () => {
      setQuestions([]);
      setAnswers({});
    };
  }, [assessmentId]);

  // ---------- Loader skeletons ----------
  const QuestionsSkeleton = ({ count = 3 }) => (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          {[...Array(count)].map((_, i) => (
            <Box key={i}>
              <Skeleton variant="text" width="60%" height={28} />
              <Skeleton variant="text" width="30%" height={20} />
              <Stack sx={{ mt: 1 }}>
                <Skeleton variant="rectangular" height={18} sx={{ mb: 1, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={18} sx={{ mb: 1, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={18} sx={{ mb: 1, borderRadius: 1 }} />
              </Stack>
              {i !== count - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );

  const HeaderSkeleton = () => (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, mt: 2 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Skeleton variant="text" width={220} height={32} />
      </Stack>
      <Skeleton variant="text" width={280} height={18} />
      <Skeleton variant="text" width={340} height={18} />
      <Skeleton variant="rectangular" height={10} sx={{ mt: 1.5, borderRadius: 2 }} />
    </Box>
  );

  function renderMathText(text) {
    // Return early if no math signals are found to optimize performance
    if (!text || (!text.includes('$'))) {
      return <span>{text}</span>;
    }
  
    // Split by:
    // 1. Block Math: $$...$$ (using [\s\S] to match newlines inside blocks)
    // 2. Inline Math: $...$
    const regex = /(\$\$[\s\S]+?\$\$|\$[^$]+\$)/g;
    
    const parts = text.split(regex);
  
    return parts.map((part, i) => {
      // 1. Handle Block Math ($$ content $$)
      if (part.startsWith('$$') && part.endsWith('$$')) {
        // Remove the first 2 and last 2 characters
        return <BlockMath key={i} math={part.slice(2, -2)} />;
      }
      // 2. Handle Inline Math ($ content $)
      else if (part.startsWith('$') && part.endsWith('$')) {
        // Remove the first 1 and last 1 character
        return <InlineMath key={i} math={part.slice(1, -1)} />;
      }
      // 3. Handle Text (prevent returning empty spans from split artifacts)
      else if (part.length > 0) {
        return <span key={i}>{part}</span>;
      }
      
      return null;
    });
  }

  const renderParagraphsWithMath = (text, renderMathText, isFirstElement, questionNumber) => {
    if (!text) return null;
    
    // 1. Split text by double newline (\n\n or \n \n) to respect explicit paragraph breaks.
    //    This regex handles one or more newlines optionally separated by whitespace.
    const paragraphs = text.split(/\n\s*\n/g).filter(p => p.trim() !== "");
  
    return paragraphs.map((paragraph, pIdx) => (
      <Typography
        key={pIdx}
        // Use h6/bold for the first paragraph, and body1/regular for subsequent paragraphs.
        // This is necessary because the single Typography element for the question text is replaced.
        variant={isFirstElement && pIdx === 0 ? "h6" : "body1"}
        fontWeight={isFirstElement && pIdx === 0 ? 600 : 400}
        gutterBottom
        sx={{ 
          mt: pIdx > 0 ? 1 : 0, // Add top margin for separation between paragraphs
          lineHeight: 1.5, // Improve readability for long text
        }}
      >
        {/* Prepend the question number ONLY to the very first paragraph of the question */}
        
        {renderMathText(paragraph)}
      </Typography>
    ));
  };

  const progress =
    questions.length > 0 ? (Object.keys(answers).length / questions.length) * 100 : 0;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      {/* SUBMITTING OVERLAY */}
      <Backdrop open={submitting} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <Stack alignItems="center" spacing={1.5}>
          <CircularProgress />
          <Typography variant="body2">Saving your responses…</Typography>
        </Stack>
      </Backdrop>

      {/* COMPLETION MESSAGE */}
      {completed && <AssessmentCompletionMessage />}

      {/* INITIAL LOADERS */}
      {!completed && isLoading && (
        <Box>
          <HeaderSkeleton />
          <Box sx={{ mt: 2 }}>
            <QuestionsSkeleton count={3} />
          </Box>
        </Box>
      )}

      {/* CONTENT */}
      {!completed && !isLoading && (
        <>
          {/* Pre-assessment gate */}
          {preAssessment && !preAssessmentCompleted ? (
            <StudentPreAssessmentForm student={student} onSubmit={handlePreAssessmentFormSubmit} />
          ) : (
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, mt: 2 }}>
                <SchoolOutlined color="primary" />
                <Typography variant="h5" fontWeight="bold">
                  Online Assessment
                </Typography>
              </Stack>

              {sessionId && isValidUUID(sessionId) && (
                <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                  Session ID: {sessionId}
                </Typography>
              )}
              {student && (
                <Typography color="text.secondary" variant="subtitle2" gutterBottom>
                  Student: {student.first_name} {student.middle_name} {student.last_name}
                </Typography>
              )}

              <Divider sx={{ my: 1 }} />

              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" align="center" sx={{ mb: 0.5 }}>
                  Progress: {Object.keys(answers).length}/{questions.length} answered
                </Typography>
                <LinearProgress
                  color="secondary"
                  variant="determinate"
                  value={progress}
                  sx={{ height: 10, borderRadius: 2 }}
                />
              </Box>

              <Card>
                <CardContent>
                  {error.open && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      <AlertTitle>
                        <Typography fontWeight="bold">{error.title}</Typography>
                      </AlertTitle>
                      <Typography variant="body2">{error.body}</Typography>
                    </Alert>
                  )}

                  <List>
                    {questions && questions.map((question, idx) => (
                      <React.Fragment key={question.question_id}>
                        <ListItem sx={{ py: 4, flexDirection: "column", alignItems: "flex-start" }}>
                          {question.image_url && question.image_url !== "NA" && (
                            <Box
                              sx={{
                                mb: 2,
                                display: "flex",
                                justifyContent: "center",
                                width: "100%",
                              }}
                            >
                              <img
                                src={question.image_url}
                                alt={`Question ${idx + 1}`}
                                onClick={() => handleImageClick(question.image_url)}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: 300,
                                  objectFit: "contain",
                                  borderRadius: 8,
                                  border: "1px solid #ccc",
                                  cursor: "zoom-in",
                                }}
                              />
                            </Box>
                          )}

                          <Typography 
                            gutterBottom
                            sx={{ whiteSpace: 'pre-wrap' }} 
                            variant="h6" fontWeight={600}>
                            {idx + 1}. {renderParagraphsWithMath(question.question_text, renderMathText,  idx === 0 ? true: false, question.order_number )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                            Type: {question.question_type} | Points: {question.points}
                          </Typography>

                          {(question.question_type === "multiple_choice" ||
                            question.question_type === "true_false") && (
                            <RadioGroup
                              value={answers[question.question_id] || ""}
                              onChange={(e) =>
                                handleChoiceSelect(question.question_id, parseInt(e.target.value))
                              }
                              sx={{ width: "100%" }}
                            >
                              {question.choices.map((choice, i) => (
                                <FormControlLabel
                                  key={choice.choice_id}
                                  value={choice.choice_id}
                                  control={<Radio color="secondary" />}
                                  label={`${String.fromCharCode(65 + i)}. ${choice.choice_text}`}
                                  sx={{ mb: 1, pl: 2 }}
                                />
                              ))}
                            </RadioGroup>
                          )}

                          {question.question_type === "multi_select_choice" && (
                            <Stack direction="column" spacing={0.5} sx={{ mt: 1 }}>
                              <FormGroup>
                                {question.choices.map((choice, i) => (
                                  <FormControlLabel
                                    key={choice.choice_id}
                                    control={
                                      <Checkbox
                                        color="secondary"
                                        size="small"
                                        checked={
                                          answers[question.question_id]?.includes(choice.choice_id) || false
                                        }
                                        onChange={(e) =>
                                          handleMultiSelect(
                                            question.question_id,
                                            choice.choice_id,
                                            e.target.checked
                                          )
                                        }
                                      />
                                    }
                                    label={`${String.fromCharCode(65 + i)}. ${choice.choice_text}`}
                                  />
                                ))}
                              </FormGroup>
                            </Stack>
                          )}

                          {question.question_type === "short_answer" && (
                            <TextField
                              value={answers[question.question_id] || ""}
                              onChange={(e) => handleChoiceSelect(question.question_id, e.target.value)}
                              rows={5}
                              multiline
                              fullWidth
                              placeholder="Your answer..."
                              color="secondary"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </ListItem>
                        {idx !== questions.length - 1 && <Divider sx={{ my: 2 }} />}
                      </React.Fragment>
                    ))}
                  </List>

                  <Stack direction="row" justifyContent="center" sx={{ mt: 4 }}>
                    <Button
                      onClick={saveAssessment}
                      variant="contained"
                      color="primary"
                      startIcon={!submitting && <CheckCircleOutline />}
                      disabled={error.open || submitting}
                      size="large"
                    >
                      {submitting ? (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CircularProgress size={20} />
                          <span>Submitting…</span>
                        </Stack>
                      ) : (
                        'Submit Answers'
                      )}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          )}
        </>
      )}

      {/* Image Modal */}
      <Modal
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box sx={{ maxWidth: "90%", maxHeight: "90%", outline: "none" }}>
          <img
            src={selectedImage}
            alt="Enlarged preview"
            style={{
              width: "100%",
              height: "auto",
              borderRadius: 8,
              boxShadow: "0 0 20px rgba(0,0,0,0.5)",
            }}
          />
        </Box>
      </Modal>
    </Container>
  );
};

export default AssessmentTaker;
